import { ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { BTStatusBadge } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsActionBar, BdsButtonGroup, BdsRollupButton } from "@buildertrend/components";

import { BTIconPushpinFilled } from "commonComponents/btWrappers/BTIcon";
import { BTTabs } from "commonComponents/btWrappers/BTTabs/BTTabs";
import { ButtonEmbedded } from "commonComponents/utilities/ButtonEmbedded/ButtonEmbedded";

import "./TabsFilter.less";

export enum TabsFilterTabs {
    inbox = "inbox",
    inReview = "inReview",
    readyForPayment = "readyForPayment",
    paid = "paid",
    allBills = "allBills",
}

export const isTabsFilterTab = (value: unknown): value is TabsFilterTabs =>
    typeof value === "string" && (Object.values(TabsFilterTabs) as string[]).includes(value);

export interface ITabCounts {
    draftBillsCount: number;
    inReviewBillsCount: number;
    readyForPaymentBillsCount: number;
    paidBillsCount: number;
    otherBillsCount: number;
}

export interface ITabsFilterProps {
    currentlySelected: TabsFilterTabs;
    onTabSelected: (tab: TabsFilterTabs) => void;
    hasDraftOnlyAccess?: boolean;
    counts?: ITabCounts;
    /**
     * The tab the user has pinned as their default. Null means no preference — the app falls back to its
     * built-in default.
     */
    defaultTab: TabsFilterTabs | null;
    /**
     * Called when the user pins a tab as their default, or clicks the pin on the current default to clear it.
     * Pass `null` to clear the preference.
     */
    onDefaultTabChanged: (tab: TabsFilterTabs | null) => void;
}

const formatCount = (count: number | undefined): string | null => {
    if (count === undefined || count === null) {
        return null;
    }
    return count.toLocaleString();
};

const getCountForTab = (tab: TabsFilterTabs, counts?: ITabCounts): string | null => {
    if (!counts) {
        return null;
    }
    switch (tab) {
        case TabsFilterTabs.inbox:
            return formatCount(counts.draftBillsCount);
        case TabsFilterTabs.inReview:
            return formatCount(counts.inReviewBillsCount);
        case TabsFilterTabs.readyForPayment:
            return formatCount(counts.readyForPaymentBillsCount);
        case TabsFilterTabs.paid:
            return formatCount(counts.paidBillsCount);
        case TabsFilterTabs.allBills: {
            // "All Bills" excludes drafts by design — drafts live in the Inbox tab and are not considered bills until promoted
            const total =
                counts.inReviewBillsCount +
                counts.readyForPaymentBillsCount +
                counts.paidBillsCount +
                counts.otherBillsCount;
            return formatCount(total);
        }
        default:
            return null;
    }
};

const TabsFilter = ({
    currentlySelected,
    onTabSelected,
    hasDraftOnlyAccess,
    counts,
    defaultTab,
    onDefaultTabChanged,
}: ITabsFilterProps) => {
    const isXsScreen = useMediaQuery(`(max-width: ${ScreenMax.xs}px)`);

    if (hasDraftOnlyAccess) {
        if (isXsScreen) {
            return (
                <BdsActionBar testid="tabsFilterMobileActionBar">
                    <BdsButtonGroup
                        id="tabsFilterMobileActionButtonGroup"
                        grow
                        menuTestid="selectableMenu"
                        rollupTestid="selectableRollup"
                        selectable
                        reverse
                        compact
                        testid="selectable"
                        displayType="tertiary"
                        className="margin-bottom-sm"
                    >
                        <BdsRollupButton
                            id={TabsFilterTabs.inbox}
                            maxStage={0}
                            onClick={() => onTabSelected(TabsFilterTabs.inbox)}
                            testid={TabsFilterTabs.inbox}
                            text="Inbox"
                            value={TabsFilterTabs.inbox}
                            selected={true}
                            displayType="tertiary"
                        />
                    </BdsButtonGroup>
                </BdsActionBar>
            );
        }

        return (
            <BTTabs
                data-testid="test-tabs"
                defaultActiveKey={TabsFilterTabs.inbox}
                activeKey={TabsFilterTabs.inbox}
                onChange={() => onTabSelected(TabsFilterTabs.inbox)}
            >
                <BTTabs.TabPane
                    tab={
                        <div className="flex flex-row" style={{ gap: 4 }}>
                            Inbox
                        </div>
                    }
                    key={TabsFilterTabs.inbox}
                />
            </BTTabs>
        );
    }

    if (isXsScreen) {
        const renderButton = (id: TabsFilterTabs, text: string) => {
            const count = getCountForTab(id, counts);
            return (
                <BdsRollupButton
                    id={id}
                    maxStage={0}
                    onClick={() => onTabSelected(id)}
                    testid={id}
                    text={count ? `${text} (${count})` : text}
                    value={id}
                    selected={currentlySelected === id}
                    displayType="tertiary"
                />
            );
        };

        return (
            <BdsActionBar testid="tabsFilterMobileActionBar">
                <BdsButtonGroup
                    id="tabsFilterMobileActionButtonGroup"
                    grow
                    menuTestid="selectableMenu"
                    rollupTestid="selectableRollup"
                    selectable
                    reverse
                    compact
                    testid="selectable"
                    displayType="tertiary"
                    className="margin-bottom-sm"
                >
                    {renderButton(TabsFilterTabs.allBills, "All Bills")}
                    {renderButton(TabsFilterTabs.paid, "Paid")}
                    {renderButton(TabsFilterTabs.readyForPayment, "Ready for Payment")}
                    {renderButton(TabsFilterTabs.inReview, "In Review")}
                    {renderButton(TabsFilterTabs.inbox, "Inbox")}
                </BdsButtonGroup>
            </BdsActionBar>
        );
    }

    const renderTab = (tab: TabsFilterTabs, name: string) => {
        const count = getCountForTab(tab, counts);
        const isDefault = defaultTab === tab;
        const pinClassName = cn("TabsFilter-tabLabel-pushpin", {
            pinned: isDefault,
        });
        return (
            <span className="TabsFilter-tabLabel">
                {name}
                {count && (
                    <BTStatusBadge
                        testid={`${tab}-count`}
                        displayType="default"
                        textOnly
                        text={count}
                    />
                )}
                <ButtonEmbedded
                    data-testid={`${tab}-default-pin`}
                    aria-label={
                        isDefault
                            ? `${name} is your default tab. Click to remove.`
                            : `Set ${name} as your default tab`
                    }
                    title={isDefault ? "Default tab" : "Set as default tab"}
                    onKeyDown={(e) => {
                        // ButtonEmbedded handles activation on keyup, but BTTabs reacts to keydown — without
                        // this, Enter/Space on a focused pin also triggers a tab change.
                        if (e.key === "Enter" || e.key === " ") {
                            e.stopPropagation();
                        }
                    }}
                    onActionPerformed={(e) => {
                        // Prevent BTTabs from interpreting the pin click as a tab change.
                        e.stopPropagation();
                        if (isDefault) {
                            // Clearing the preference — drop focus so :focus-within stops keeping the
                            // (now non-pinned) icon visible after the click.
                            (e.currentTarget as HTMLElement).blur?.();
                        }
                        onDefaultTabChanged(isDefault ? null : tab);
                    }}
                >
                    <span className={pinClassName}>
                        <BTIconPushpinFilled />
                    </span>
                </ButtonEmbedded>
            </span>
        );
    };

    return (
        <BTTabs
            data-testid="test-tabs"
            defaultActiveKey={TabsFilterTabs.inReview}
            activeKey={currentlySelected}
            onChange={(activeKey: string) => onTabSelected(activeKey as TabsFilterTabs)}
        >
            <BTTabs.TabPane
                tab={renderTab(TabsFilterTabs.inbox, "Inbox")}
                key={TabsFilterTabs.inbox}
            />
            <BTTabs.TabPane
                tab={renderTab(TabsFilterTabs.inReview, "In Review")}
                key={TabsFilterTabs.inReview}
            />
            <BTTabs.TabPane
                tab={renderTab(TabsFilterTabs.readyForPayment, "Ready for Payment")}
                key={TabsFilterTabs.readyForPayment}
            />
            <BTTabs.TabPane
                tab={renderTab(TabsFilterTabs.paid, "Paid")}
                key={TabsFilterTabs.paid}
            />
            <BTTabs.TabPane
                tab={renderTab(TabsFilterTabs.allBills, "All Bills")}
                key={TabsFilterTabs.allBills}
            />
        </BTTabs>
    );
};

export default TabsFilter;
